import { storedRef } from "../../lib/referral";
import { useEffect, useState } from "react";
import styled from "styled-components";
import {
  actionStyle,
  controlStyle,
  ActionVariant,
} from "../../styles/controls";
import { apiFetch } from "../../lib/api";
import { mapBackendUserToApp, BackendAuthUser } from "../../lib/authUser";
import { useAuthStore } from "../../store/authStore";
export const Page = styled.div`
  width: 100%;
  max-width: 1320px;
  margin: 0 auto;
  display: grid;
  gap: 24px;
  color: ${(p) => p.theme.colors.text};
  min-width: 0;
  align-content: start;
  h1 {
    font-size: clamp(26px, 3vw, 36px);
    line-height: 1.15;
    letter-spacing: -0.035em;
    font-weight: 750;
  }
  h2 {
    font-size: 19px;
    line-height: 1.35;
    letter-spacing: -0.025em;
    font-weight: 650;
  }
  h3 {
    font-size: 15px;
    line-height: 1.4;
    font-weight: 650;
  }
  p {
    line-height: 1.65;
  }
  a {
    color: ${(p) => p.theme.colors.primary};
    transition: color 0.18s;
  }
  a:hover {
    color: ${(p) => p.theme.colors.primaryHover};
  }
  [role="alert"] {
    padding: 12px 16px;
    border-radius: 10px;
    background: ${(p) => p.theme.colors.dangerSoft};
    color: ${(p) => p.theme.colors.danger};
    border: 1px solid #ff737330;
  }
  table {
    border-collapse: collapse;
    font-size: 15px;
    white-space: nowrap;
  }
  th {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    color: ${(p) => p.theme.colors.textMuted};
    font-weight: 600;
    padding: 12px 10px;
    border-bottom: 1px solid ${(p) => p.theme.colors.border};
  }
  td {
    padding: 14px 10px !important;
    border-bottom: 1px solid ${(p) => p.theme.colors.border};
    font-variant-numeric: tabular-nums;
  }
  td button {
    min-height: 32px;
    min-width: 32px;
    padding: 7px;
  }
  td a[title] {
    display: inline-flex;
    padding: 8px;
    border-radius: 8px;
    background: #ffffff06;
  }
  tbody tr:last-child td {
    border-bottom: 0;
  }
  tbody tr:hover {
    background: #ffffff03;
  }
  code {
    background: ${(p) => p.theme.colors.input};
    border: 1px solid ${(p) => p.theme.colors.border};
    border-radius: 10px;
    padding: 16px;
    font-size: 15px;
  }
  @media (max-width: 600px) {
    gap: 18px;
  }
`;
export const Panel = styled.section`
  background: ${(p) => p.theme.colors.surface};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 16px;
  padding: clamp(18px, 2.2vw, 28px);
  display: grid;
  gap: 18px;
  min-width: 0;
  align-content: start;
  box-shadow:
    0 4px 20px #00000012,
    inset 0 1px 0 #ffffff03;
  > section {
    min-width: 0;
  }
`;
export const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  min-width: 0;
  > input {
    flex: 1 1 160px;
  }
  > a {
    overflow-wrap: anywhere;
  }
`;
export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
  gap: 20px;
  align-items: start;
  min-width: 0;
`;
export const Field = styled.input`
  ${controlStyle}
`;
export const TextArea = styled.textarea`
  ${controlStyle} min-height: 130px;
  resize: vertical;
`;
export const Select = styled.select`
  ${controlStyle} cursor: pointer;
  padding-right: 32px;
`;
export const Action = styled.button<{ $variant?: ActionVariant }>`
  ${actionStyle}
`;
export const Note = styled.p`
  color: ${(p) => p.theme.colors.textMuted};
  font-size: 15px;
  line-height: 1.65;
`;
export const Eyebrow = styled.span`
  color: ${(p) => p.theme.colors.primary};
  font-size: 12px;
  font-weight: 750;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;
export const PageHeading = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  > div {
    display: grid;
    gap: 8px;
  }
`;
export const FormField = styled.label`
  display: grid;
  align-content: start;
  gap: 8px;
  min-width: 0;
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: 14px;
  font-weight: 550;
  > input {
    font-weight: 400;
  }
`;
export const Badge = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) =>
    p.$active ? p.theme.colors.success : p.theme.colors.textSecondary};
  background: ${(p) => (p.$active ? "#73d6a210" : p.theme.colors.surfaceHover)};
  border: 1px solid ${(p) => (p.$active ? "#73d6a230" : p.theme.colors.border)};
`;
export const EmptyState = styled.div`
  display: grid;
  justify-items: center;
  text-align: center;
  gap: 12px;
  padding: 24px 16px;
  border: 0;
  border-radius: 14px;
  color: ${(p) => p.theme.colors.textMuted};
  > svg {
    width: 30px;
    height: 30px;
    color: ${(p) => p.theme.colors.textMuted};
  }
`;
export const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  color: ${(p) => p.theme.colors.textMuted};
  font-size: 13px;
  &:empty {
    gap: 0;
  }
  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${(p) => p.theme.colors.border};
  }
`;
export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}
/* Тексты для сбоев, у которых нет внятного сообщения от сервера. request() живёт вне React, поэтому язык берётся из того же ключа, что и у LanguageContext. */
const SYSTEM_TEXT = {
  ru: {
    offline: "Нет соединения с сервером. Проверьте сеть и повторите.",
    temporary: "Сервис временно недоступен. Попробуйте ещё раз.",
    generic: "Не удалось выполнить запрос",
  },
  en: {
    offline: "No connection to the server. Check your network and try again.",
    temporary: "The service is temporarily unavailable. Please try again.",
    generic: "The request could not be completed",
  },
};
function uiLanguage(): "ru" | "en" {
  try {
    return localStorage.getItem("app_language") === "en" ? "en" : "ru";
  } catch {
    return "ru";
  }
}
/** Сообщение, которое можно показать пользователю: сырой английский текст прокси или стек не выводятся. */
export function apiErrorText(status: number, serverMessage?: unknown): string {
  const lang = uiLanguage();
  const text = typeof serverMessage === "string" ? serverMessage.trim() : "";
  if (status >= 500 && (!text || lang === "en" || !/[А-Яа-яЁё]/.test(text))) return SYSTEM_TEXT[lang].temporary;
  return text || SYSTEM_TEXT[lang].generic;
}
export async function request<T = any>(
  path: string,
  body?: unknown,
  method?: string,
  signal?: AbortSignal,
): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(path, {
      method: method || (body === undefined ? "GET" : "POST"),
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch (e) {
    if ((e as Error)?.name === "AbortError") throw e;
    throw new ApiError(SYSTEM_TEXT[uiLanguage()].offline, 0);
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new ApiError(
      apiErrorText(res.status, data?.error),
      res.status,
      typeof data?.code === "string" ? data.code : undefined,
    );
  return data;
}
export async function refreshUser() {
  const raw = await request<BackendAuthUser>("/auth/me");
  useAuthStore
    .getState()
    .updateUser(mapBackendUserToApp(raw, useAuthStore.getState().user?.stats));
}
export function acceptSession(data: { user: BackendAuthUser; token: string }) {
  localStorage.setItem("auth-token", data.token);
  useAuthStore.getState().login(mapBackendUserToApp(data.user));
}
// Consents are sent only from the sign-up form, where the user has ticked them; elsewhere Steam may only sign in or link.
export async function steamLogin(consent?: { terms: boolean; personalData: boolean }) {
  const data = await request<{ url: string }>("/auth/steam/start", { ref: storedRef(), ...(consent ?? {}) });
  window.location.assign(data.url);
}
export function usePoll<T>(
  path: string | null,
  interval = 3000,
  paused = false,
) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    setData(null);
    setError("");
    setErrorStatus(null);
  }, [path]);
  useEffect(() => {
    if (!path || paused) return;
    let alive = true,
      timer: ReturnType<typeof setTimeout>;
    async function poll() {
      try {
        const next = await request<T>(path!);
        if (alive) {
          setData(next);
          setError("");
          setErrorStatus(null);
        }
      } catch (e) {
        if (alive) {
          setError((e as Error).message);
          setErrorStatus(e instanceof ApiError ? e.status : 0);
        }
      }
      if (alive) timer = setTimeout(poll, interval);
    }
    poll();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [path, interval, paused, nonce]);
  return {
    data,
    error,
    /** HTTP-статус последней ошибки (0 — сеть/разбор), null — ошибки нет. */
    errorStatus,
    setData,
    reload: () => setNonce((n) => n + 1),
    loading: data === null && !error,
  };
}
